import { createContext } from "react";
import { QuickCheckoutCartApiStatus } from "@swiggy-private/cart-types";
import { QC_FOOTER_TYPE, QC_FOOTER_TYPE_PAYLOAD } from "@swiggy-private/checkout-core";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

export interface QuickCheckoutContextValue {
    /**
     * Unified update-cart lifecycle status for quick checkout. Written by both the
     * direct-bridge (prefetch inline hydration) flow and the JS-bridge flow, and read by
     * the renderer to decide shimmer vs failed vs cart. Cart data itself lives in
     * CartContext, so this context stays focused on the lifecycle status only.
     */
    updateCartApiState: QuickCheckoutCartApiStatus;
    setUpdateCartApiState: (status: QuickCheckoutCartApiStatus) => void;
    refreshParentScreenOnRemount: boolean;
    setRefreshParentScreenOnRemount: (refresh: boolean) => void;
    /**
     * Epoch-ms of the last success cart applied from either transport. Used to drop stale
     * success payloads when prefetch and the JS bridge race. `null` until the first success.
     */
    getLastAppliedCartUpdateAt: () => number | null;
    /**
     * Records a success cart timestamp after it has been accepted. Callers must gate with
     * `isNewerQuickCheckoutCartUpdate` first so an older clock cannot move this forward.
     */
    setLastAppliedCartUpdateAt: (timestampMs: number) => void;
    /**
     * True once the JS bridge has delivered any lifecycle update. Prefetch success must not
     * overwrite a bridge-driven loading/failed that arrived before hydrate completed.
     */
    hasJsBridgeLifecycleUpdate: () => boolean;
    /** Desired collapsed QC webview scale; survives QC ↔ MRO route swaps. */
    qcFooterType: QC_FOOTER_TYPE;
    /**
     * Computes footer scale via the provider (single source of `getQuickCheckoutWebviewScale`).
     * Pass a cart after `await doUpdateCart` so async callers do not read a stale closed-over
     * `qcFooterType` before the next React render. Omit args to use the latest context cart.
     */
    getQcFooterType: (reviewedCart?: MROReviewedCart | null, isLoading?: boolean) => QC_FOOTER_TYPE;
    /** ContentWrapper height (DP) derived from qcFooterType. */
    qcFooterContentHeight: number;
}

export const QuickCheckoutContext = createContext<QuickCheckoutContextValue>({
    updateCartApiState: QuickCheckoutCartApiStatus.LOADING,
    setUpdateCartApiState: () => void 0,
    refreshParentScreenOnRemount: false,
    setRefreshParentScreenOnRemount: () => void 0,
    getLastAppliedCartUpdateAt: () => null,
    setLastAppliedCartUpdateAt: () => void 0,
    hasJsBridgeLifecycleUpdate: () => false,
    // added for footer height
    qcFooterType: QC_FOOTER_TYPE.LOADING,
    getQcFooterType: () => QC_FOOTER_TYPE.LOADING,
    qcFooterContentHeight: QC_FOOTER_TYPE_PAYLOAD[QC_FOOTER_TYPE.LOADING].height,
});

if (__DEV__) {
    QuickCheckoutContext.displayName = "QuickCheckoutContext";
}
